import React, { useState, useRef, useEffect } from 'react';
import { IoSend } from 'react-icons/io5';
import { GrMicrophone } from 'react-icons/gr';

type Message = {
  text: string;
  sender: 'user' | 'ai';
  isExample?: boolean;
};

const GuideIntelligence: React.FC = () => {
  const [inputValue, setInputValue] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const conversationRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the input field on component mount
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages are added
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    setChatHistory([...chatHistory, { text: inputValue, sender: 'user' }]);
    
    // Simulate AI response (in a real app, this would be an API call)
    setTimeout(() => {
      setChatHistory(prev => [
        ...prev,
        { text: "Je suis Guide Intelligence et je vais vous aider avec votre question. Pouvez-vous me donner plus de détails?", sender: 'ai' }
      ]);
    }, 1000);
    
    setInputValue('');
  };

  return (
    <div className="bg-[#fefaf6] w-full h-auto">
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
      <div className="flex items-center gap-2 mb-2">
        <img 
          src="/images/guide-intelligence.png" 
          alt="Guide Pro" 
          className="w-12 h-12 sm:w-14 sm:h-14 object-contain" 
        />
        <div className='py-5'>
          <h1 className="text-2xl font-bold text-gray-800">Guide Intelligence</h1>
        </div>
      </div>
      <p className="text-md text-gray-600 mb-4">Apprenez davantage et soyez encore plus performant grâce à l'assistant spécial IA de votre plateforme GUIDE PRO.</p>
        <div className="flex flex-col bg-white rounded-lg overflow-hidden shadow-md h-[80vh]">
          {/* Header */}
          <div className="p-3 sm:p-4 bg-white shadow-sm">
            <h3 className="mt-1 sm:mt-2 sm:text-center text-gray-600 text-xl font-bold">Comment puis-je vous guider?</h3>
          </div>
          
          {/* Conversation Area */}
          <div 
            ref={conversationRef}
            className="flex-1 p-3 sm:p-4 overflow-y-auto"
          >
            {chatHistory.length === 0 ? (
              <div className="text-center text-gray-500 mt-4">
                Posez une question pour commencer la conversation.
              </div>
            ) : (
              <div className="space-y-4">
                {chatHistory.map((message, index) => {
                  let widthClass = 'max-w-[75%] md:max-w-[50%] lg:max-w-[40%]';
                  
                  if (message.isExample) {
                    widthClass = 'max-w-fit';
                  }
                  
                  return (
                    <div
                      key={index}
                      className={`${
                        message.sender === 'user'
                          ? 'ml-auto bg-orange-200 text-gray'
                          : message.isExample
                          ? 'mr-auto bg-amber-200 text-amber-800 cursor-pointer hover:bg-amber-300'
                          : 'mr-auto bg-gray-100 text-gray-800'
                      } ${widthClass} p-3 rounded-lg break-words`}
                      onClick={() => {
                        if (message.isExample) {
                          setChatHistory([
                            ...chatHistory.filter(m => !m.isExample),
                            { text: message.text, sender: 'user' }
                          ]);
                          
                          // Simulate AI response
                          setTimeout(() => {
                            setChatHistory(prev => [
                              ...prev,
                              { text: "Je suis Guide Intelligence et je vais vous aider avec votre question. Pouvez-vous me donner plus de détails?", sender: 'ai' }
                            ]);
                          }, 1000);
                        }
                      }}
                    >
                      {message.text}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Input Area */}
          <div className="p-2 sm:p-4 border-t shadow-md bg-white flex-shrink-0">
            <form onSubmit={handleSubmit} className="flex gap-1 sm:gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Une question..."
                className="flex-1 py-2 px-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
              />
              <button
                type="button"
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 hidden sm:block"
              >
                <GrMicrophone size={20} />
              </button>
              <button
                type="submit"
                className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                disabled={!inputValue.trim()}
              >
                <IoSend size={18} className="sm:w-5 sm:h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideIntelligence; 